import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Production from './pages/Production';
import Inventory from './pages/Inventory';
import Transfers from './pages/Transfers';
import Wastage from './pages/Wastage';
import Reports from './pages/Reports';
import AdminSetup from './pages/AdminSetup';
import POS from './pages/POS';
import Menu from './pages/Menu';
import Sales from './pages/Sales';
import Settings from './pages/Settings';
import Orders from './pages/Orders';
import Customers from './pages/Customers';
import LocationChooser from './pages/LocationChooser';
import { LocationContext } from './context/LocationContext';
import { Toaster } from './components/ui/sonner';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const hasSession = typeof window !== 'undefined' && window.location.hash.includes('session_id=');
  const [processingAuth, setProcessingAuth] = useState(hasSession);
  const [authError, setAuthError] = useState('');
  const [authReady, setAuthReady] = useState(false);
  const [allowedLocations, setAllowedLocations] = useState([]);
  const [activeLocation, setActiveLocationState] = useState(() => {
    try {
      const stored = localStorage.getItem('active_location');
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      localStorage.removeItem('active_location');
      return null;
    }
  });
  const [locationReady, setLocationReady] = useState(false);
  const processedRef = useRef(false);

  // Sliding token refresh + transparent 401 retry so shop-floor users never lose work to an expired token.
  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    let refreshing = null;
    let reauthing = false;
    const forceReauth = () => {
      if (reauthing) return;
      reauthing = true;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('active_location');
      // Order draft (roch_order_draft) is intentionally kept so in-progress work survives re-login.
      if (!window.location.pathname.includes('/login')) window.location.href = '/login';
    };
    const doRefresh = () => {
      const token = localStorage.getItem('token');
      if (!token) return Promise.resolve(null);
      if (!refreshing) {
        refreshing = axios.post(`${API}/auth/refresh`, {}, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => {
            localStorage.setItem('token', r.data.token);
            if (r.data.user) localStorage.setItem('user', JSON.stringify(r.data.user));
            return r.data.token;
          })
          .catch(() => null)
          .finally(() => { refreshing = null; });
      }
      return refreshing;
    };

    const interceptorId = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const orig = error.config;
        const status = error.response?.status;
        const detail = error.response?.data?.detail;
        const isAuthError = status === 401 && ['Token expired', 'Invalid token', 'Not authenticated', 'User not found'].includes(detail);
        if (isAuthError && orig && !orig._retried && !String(orig.url || '').includes('/auth/refresh')) {
          orig._retried = true;
          const newToken = detail === 'Token expired' ? await doRefresh() : null;
          if (newToken) {
            orig.headers = { ...(orig.headers || {}), Authorization: `Bearer ${newToken}` };
            return axios(orig);
          }
          // Token is dead and can't be refreshed → send the user to log in again (draft is preserved).
          forceReauth();
        }
        return Promise.reject(error);
      }
    );

    doRefresh();
    const onVisible = () => { if (document.visibilityState === 'visible') doRefresh(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    const interval = setInterval(doRefresh, 6 * 60 * 60 * 1000);

    return () => {
      axios.interceptors.response.eject(interceptorId);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(interval);
    };
  }, []);

  const setActiveLocation = (loc) => {
    if (loc) localStorage.setItem('active_location', JSON.stringify(loc));
    else localStorage.removeItem('active_location');
    setActiveLocationState(loc);
  };

  useEffect(() => {
    if (hasSession) return; // skip localStorage path while exchanging Google session
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
    }
    setAuthReady(true);
  }, []);

  // Resolve which locations this user can operate; auto-select when only one.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    setLocationReady(false);
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    (async () => {
      try {
        const res = await axios.get(`${API}/locations`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const operational = res.data.filter((l) => ['outlet', 'central_kitchen'].includes(l.type));
        const allowed = user.role === 'admin'
          ? operational
          : operational.filter((l) => l.location_id === user.outlet_id);
        setAllowedLocations(allowed);
        const stored = localStorage.getItem('active_location');
        const storedLoc = stored ? JSON.parse(stored) : null;
        const validStored = storedLoc && allowed.find((l) => l.location_id === storedLoc.location_id);
        if (validStored) {
          setActiveLocationState(storedLoc);
        } else if (allowed.length === 1) {
          setActiveLocation(allowed[0]);
        } else {
          setActiveLocation(null);
        }
      } catch (e) {
        setAllowedLocations([]);
      } finally {
        setLocationReady(true);
      }
    })();
  }, [isAuthenticated, user?.phone, user?.email]);

  useEffect(() => {
    if (!hasSession || processedRef.current) return;
    processedRef.current = true;
    const params = new URLSearchParams(window.location.hash.substring(1));
    const sessionId = params.get('session_id');
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    (async () => {
      try {
        const res = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
        handleLogin(res.data.token, res.data.user);
      } catch (e) {
        setAuthError(e.response?.data?.detail || 'Google login failed. Please try again.');
      } finally {
        window.history.replaceState(null, '', window.location.pathname);
        setProcessingAuth(false);
        setAuthReady(true);
      }
    })();
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('active_location');
    setIsAuthenticated(false);
    setUser(null);
    setActiveLocationState(null);
    setAllowedLocations([]);
  };

  if (processingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center" data-testid="google-auth-loading">
          <div className="w-12 h-12 border-4 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-stone-600">Signing you in...</p>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="text-center max-w-md" data-testid="google-auth-error">
          <h2 className="font-heading text-2xl font-bold text-stone-900 mb-2">Access Denied</h2>
          <p className="text-stone-600 mb-6">{authError}</p>
          <a href="/login" className="inline-block px-6 h-10 leading-10 bg-secondary text-black rounded-sm font-semibold hover:bg-secondary-hover transition-colors">Back to Login</a>
        </div>
      </div>
    );
  }

  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-10 h-10 border-4 border-secondary border-t-transparent rounded-full animate-spin" data-testid="auth-init-loading" />
      </div>
    );
  }

  if (isAuthenticated && !locationReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-10 h-10 border-4 border-secondary border-t-transparent rounded-full animate-spin" data-testid="location-loading" />
      </div>
    );
  }

  const needsLocationChoice = isAuthenticated && !activeLocation && allowedLocations.length > 1;

  return (
    <LocationContext.Provider value={{ activeLocation, setActiveLocation, allowedLocations }}>
      <div className="App">
        <BrowserRouter>
          {needsLocationChoice ? (
            <LocationChooser locations={allowedLocations} onPick={setActiveLocation} user={user} onLogout={handleLogout} />
          ) : (
            <Routes>
              <Route path="/login" element={!isAuthenticated ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
              <Route path="/" element={isAuthenticated ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
              <Route path="/production" element={isAuthenticated ? <Production user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
              <Route path="/pos" element={isAuthenticated ? <POS user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
              <Route path="/orders" element={isAuthenticated ? <Orders user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
              <Route path="/customers" element={isAuthenticated ? <Customers user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
              <Route path="/sales" element={isAuthenticated ? <Sales user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
              <Route path="/menu" element={isAuthenticated ? <Menu user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
              <Route path="/settings" element={isAuthenticated && user?.role === 'admin' ? <Settings user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
              <Route path="/inventory" element={isAuthenticated ? <Inventory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
              <Route path="/transfers" element={isAuthenticated ? <Transfers user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
              <Route path="/wastage" element={isAuthenticated ? <Wastage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
              <Route path="/reports" element={isAuthenticated ? <Reports user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
              <Route path="/admin" element={isAuthenticated && user?.role === 'admin' ? <AdminSetup user={user} onLogout={handleLogout} /> : <Navigate to="/" />} />
            </Routes>
          )}
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </LocationContext.Provider>
  );
}

export default App;
